import { Container } from "@dojo/framework/core/Container";
import { ConnectionShape } from "../components/ConnectionShape";
import { ImageCropShape } from "../components/ImageCropShape";
import { SelectionBox } from "../components/SelectionBox";
import { IShapeRenderProps, Shape } from "../components/Shape";
import { GeometryTypes } from "../interfaces/model/enumerations/GeometryTypes";
import { ICustomGeometry } from "../interfaces/model/geometry/CustomGeometry";
import { IPresetGeometry } from "../interfaces/model/geometry/PresetGeometry";
import { IShapeGuide } from "../interfaces/model/geometry/ShapeGuide";
import { IShapeTextRectangle } from "../interfaces/model/geometry/ShapeTextRectangle";
import { getCustomGeometry, getGeometry } from "../selectors/geometry";
import { stateConstants } from "../selectors/index";
import { getGeoBoundaryMeta } from "../util";
import Injector from "@dojo/framework/core/Injector";

/**
 * Container widget hook to provide data to widget.
 * The value returned by getProperties is an object contains one two property:
 * presetGeometries - Collection of preset geometries data.
 * @param inject the object containing preset geometries
 * @param properties Current shape render properties owned by Shape
 */
function getProperties(injector: Injector<any>, properties: IShapeRenderProps) {
	const inject =  injector.get();
	let geometryParams: {
        adjHandlers: any[];
        avList?: IShapeGuide[];
        pathInfo?: { pathString: string; geoBoundaryMeta: any };
        textRect?: IShapeTextRectangle;
    } = {
    	adjHandlers: [],
    };
	const { pageObject, isMultiSelect } = properties;
	// While performing undo operation if shape is selected and removed in undo operation then here getting pageObject
	// as undefined so returning null form here.
	if (!pageObject) {
		return null;
	}
	if (pageObject.spPr.geometry) {
		if (pageObject.spPr.geometry.type === GeometryTypes.PRESET) {
			if (pageObject.spPr.geometry.presetShape) {
				const geometry = inject.presetGeometries[pageObject.spPr.geometry.presetShape] as IPresetGeometry;
				const { cx, cy } = pageObject.spPr.xfrm.ext;
				geometryParams = getGeometry({ geometry, shapeAvList: pageObject.spPr.geometry.avList, cy, cx });
			}
		} else {
			const geometry = pageObject.spPr.geometry as ICustomGeometry;
			const { cx, cy } = pageObject.spPr.xfrm.ext;
			geometryParams = getCustomGeometry({ geometry, shapeAvList: undefined, cy, cx });
		}
	}

	return {
		adjustHandlers: geometryParams.adjHandlers,
		avList: geometryParams.avList,
		isMultiSelect,
		geoBoundaryMeta: geometryParams.pathInfo ?
			geometryParams.pathInfo.geoBoundaryMeta : getGeoBoundaryMeta(pageObject.spPr.xfrm),
		pageObject,
		path: geometryParams.pathInfo ? geometryParams.pathInfo.pathString : "",
		textRect: geometryParams.textRect,
		zoomFactor: stateConstants().zoomFactor,
	};
}

/**
 * Connect the preset geometries data and Shape / selection box using DOJO 2 Container widget and
 * preset shape injector available in registry with label 'preset-shapes'
 */
export const ShapeContainer = Container(Shape, "preset-shapes", { getProperties });

/**
 * Connect the preset geometries data and Shape / selection box using DOJO 2 Container widget and
 * preset shape injector available in registry with label 'preset-shapes'
 */
export const ConnectionShapeContainer = Container(ConnectionShape, "preset-shapes", { getProperties });

/**
 * Also, create selection container for same purpose against selection box
 */
export const SelectionContainer = Container(
	SelectionBox, "preset-shapes",
	{ getProperties },
);

/** Image cropping tool also need geometry rendering aspect, thus the container */
export const ImageCropShapeContainer = Container(ImageCropShape, "preset-shapes", { getProperties });
